import { translate } from '../../../../../types/translation/Translator';
import React, { useContext, useState } from 'react';
import { ProductExportConfigurationContext } from '../../../../../context/ProductExportConfigurationContext';
import { ExportItems } from '../../../../components/ExportItems';
import { Routing } from '../../../../../util/routes.util';
import { ProductExportConfigurationRequest } from '../../../../../requests/productExportConfiguration.request';
import { useRequest } from '../../../../../requests/common/requestHook';
import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import { ActionModal } from '../../../../components/Modal';
import { Button, CancelButton } from '../../../../components/Button';
import moment from 'moment';
import { ExportUtil } from '../../../../../util/export.util';
import { ExportFile } from '../../../../../types/export/export';
import { DatePicker } from '../../../../components/DatePicker';
import { User } from '../../../../../types/user';
import { UserContext } from '../../../../../context/UserContext';
import { Checkbox } from '../../../../components/Checkbox';

interface ProductExportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  binId?: string;
}

export function ProductExportModal({ open, setOpen, binId }: ProductExportModalProps) {
  const { productExportConfigurations, productExportConfigurationsLoading } = useContext(
    ProductExportConfigurationContext,
  );

  const { currentUser } = useContext(UserContext);

  const [exportProducts, { loading }] = useRequest(ProductExportConfigurationRequest.download);
  const [includeNoStock, setIncludeNoStock] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(
    new Set([...productExportConfigurations.values()].filter(config => config.default).map(config => config.id)),
  );
  const [date, setDate] = useState<string | undefined>(undefined);

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = async () => {
    const locale = User.locale(currentUser);

    if (!selected.size) {
      const file = await exportProducts({
        timezone: moment.tz.guess(),
        locale: locale,
        date: date ? new Date(date) : undefined,
        binId,
        includeNoStock,
      });
      if (!file) return;
      ExportUtil.downloadExport(file);
    } else {
      const files: ExportFile[] = [];
      for (const configId of Array.from(selected)) {
        const file = await exportProducts({
          configId,
          timezone: moment.tz.guess(),
          locale: locale,
          date: date ? new Date(date) : undefined,
          binId,
          includeNoStock,
        });
        if (file) files.push(file);
      }
      if (!files.length) return;

      if (files.length === 1) {
        ExportUtil.downloadExport(files[0]);
      } else {
        ExportUtil.downloadZip(files, productExportConfigurations);
      }
    }

    setSelected(new Set());
    handleClose();
  };

  const footer = (
    <div className='flex justify-between'>
      <div className=''>
        <DatePicker placeholder={translate({ key: TranslationKey.date })} value={date} onChange={d => setDate(d)} />
      </div>
      <div className='flex gap-2'>
        <CancelButton onClick={handleClose} disabled={loading} />
        <Button loading={loading} text={translate({ key: TranslationKey.export })} onClick={handleDownload} />
      </div>
    </div>
  );

  return (
    <ActionModal
      footer={footer}
      title={translate({ key: TranslationKey.exportProducts })}
      open={open}
      setOpen={setOpen}
      className='max-w-[720px]'
    >
      <div className='grid gap-4'>
        <ExportItems
          items={[...productExportConfigurations.values()]}
          selected={selected}
          setSelected={setSelected}
          createPath={Routing.stock.products.settings.report.create()}
        />
        <Checkbox
          value={includeNoStock}
          onChange={setIncludeNoStock}
          label={translate({ key: TranslationKey.productExportConfigurationIncludeNoStockText })}
        />
      </div>
    </ActionModal>
  );
}
